package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui.dialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.strings.aliyahMarkerLabel
import com.eladfinish.bible.contest.data.common.stringResolver.IStringResolver
import com.eladfinish.bible.contest.deceased_name
import com.eladfinish.bible.contest.features.copyOptions.CopyOptionsAction
import com.eladfinish.bible.contest.features.copyOptions.CopyOptionsEffect
import com.eladfinish.bible.contest.features.copyOptions.CopyOptionsStore
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsConfig
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsInput
import com.eladfinish.bible.contest.features.copyOptions.ui.CopyOptionsDialog
import com.eladfinish.bible.contest.haftarah
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersAction
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersAction.UpdateFontSize
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState
import com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.dialogs.FontSizeDialog
import com.eladfinish.bible.contest.ui.components.dialogs.DialogWithInputField
import com.eladfinish.bible.contest.ui.components.dialogs.InputDialogMode
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.coroutines.flow.collectLatest
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
internal fun BrowseChaptersDialogs(
    dialogState: BrowseChaptersState.DialogState,
    onAction: (BrowseChaptersAction) -> Unit,
) {
    when (dialogState) {
        BrowseChaptersState.DialogState.None -> Unit

        is BrowseChaptersState.DialogState.FontSize -> {
            FontSizeDialog(
                initialFontSizeSp = dialogState.currentFontSize,
                onConfirm = { value ->
                    onAction(
                        UpdateFontSize(
                            value = value,
                        ),
                    )
                    onAction(
                        BrowseChaptersAction.HideDialog,
                    )
                },
                onDismiss = {
                    onAction(
                        BrowseChaptersAction.HideDialog,
                    )
                },
            )
        }

        is BrowseChaptersState.DialogState.ChooseAliyah -> {
            // The haftarah marker is embedded in the text LOCALIZED (HaftarotUseCase), so the
            // label passed on must resolve to the exact same string. CRITICAL: resolve through
            // the same StringResolver singleton that built the marker — compose's localized()
            // can disagree with it (it follows the UI context's locale, while StringResolver
            // uses the application context), which left the needle unmatched.
            val stringResolver: IStringResolver = koinInject()
            val localizedHaftarah = stringResolver.resolveString(stringDesc = SharedRes.strings.haftarah.desc())
            ChooseAliyahDialog(
                title = dialogState.title.localized(),
                onItemClick = {
                    onAction(
                        BrowseChaptersAction.AliyahClicked(
                            aliyahLabel = aliyahMarkerLabel(
                                aliyahLabel = it,
                                localizedHaftarah = localizedHaftarah,
                            ),
                        ),
                    )
                },
                onDismiss = {
                    onAction(
                        BrowseChaptersAction.HideDialog,
                    )
                },
            )
        }

        is BrowseChaptersState.DialogState.DeceasedName -> {
            DialogWithInputField(
                label = stringResource(SharedRes.strings.deceased_name),
                mode = InputDialogMode.CREATE,
                initialValue = dialogState.currentName,
                onDismiss = {
                    onAction(
                        BrowseChaptersAction.HideDialog,
                    )
                },
                onConfirm = {
                    onAction(
                        BrowseChaptersAction.DeceasedNameConfirmed(name = it),
                    )
                },
            )
        }

        is BrowseChaptersState.DialogState.BrowseCopyOptions -> {
            val store: CopyOptionsStore = koinViewModel()

            val bookAndChapterForExport = dialogState.bookAndChapterForExport.localized()

            val config = CopyOptionsConfig(
                shouldShowSearchedExpressionOption = false,
                shouldShowRemoveDuplicatesOption = false,
                shouldShowSortResultOption = false,
                shouldShowIncludeBookAndChapterOption = true,
                shouldShowHighlightsSection = false,
                shouldShowAdvancedOptionsSection = false,
            )

            LaunchedEffect(
                store,
                dialogState.text,
                bookAndChapterForExport,
                dialogState.operation,
                dialogState.shouldAddParenthesisOnOtiotPasuk,
                dialogState.documentTextType,
            ) {
                store.dispatch(
                    CopyOptionsAction.Init(
                        config = config,
                        operation = dialogState.operation,
                        input = CopyOptionsInput.PlainText(
                            rawText = dialogState.text,
                        ),
                        bookAndChapterForExport = bookAndChapterForExport + "\n\n",
                        shouldAddParenthesisOnOtiotPasuk = dialogState.shouldAddParenthesisOnOtiotPasuk,
                        documentTextType = dialogState.documentTextType,
                    ),
                )
            }

            LaunchedEffect(store) {
                store.observeSideEffect().collectLatest { effect ->
                    when (effect) {
                        CopyOptionsEffect.CloseDialog -> {
                            onAction(
                                BrowseChaptersAction.HideDialog,
                            )
                        }

                        is CopyOptionsEffect.SaveFile -> {
                            onAction(
                                BrowseChaptersAction.SaveFile(
                                    content = effect.content,
                                    bookAndChapterForExport = bookAndChapterForExport,
                                    documentTextType = dialogState.documentTextType,
                                ),
                            )
                            onAction(
                                BrowseChaptersAction.HideDialog,
                            )
                        }
                    }
                }
            }

            CopyOptionsDialog(
                store = store,
                title = dialogState.title.desc().localized(),
                onDismiss = {
                    onAction(
                        BrowseChaptersAction.HideDialog,
                    )
                },
            )
        }
    }
}
